// Slice de autenticação. Estado vem do back via /v1/me ou /v1/auth/login.
// `onboardingComplete` é derivado em separado de /v1/onboarding/status —
// armazenado aqui pra evitar bouncing.
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { normalizeRole, type Role } from '@/config/constants';
import type { AvatarTone } from '@/components/ui/Avatar';

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  role: Role;
  avatarTone: AvatarTone;
  /** URL da foto de perfil (já com token). null/ausente → avatar cai pras iniciais. */
  avatarUrl?: string | null;
  companyId: string;
  storeIds: string[];
}

export interface AuthState {
  currentUser: CurrentUser | null;
  accessToken: string | null;
  isAuthed: boolean;
  /** indica se o gestor já terminou o onboarding (apenas relevante pra GESTOR). */
  onboardingComplete: boolean;
}

const initialState: AuthState = {
  currentUser: null,
  accessToken: null,
  isAuthed: false,
  onboardingComplete: true,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    sessionStarted(state, action: PayloadAction<{ user: CurrentUser; accessToken: string }>) {
      const { user } = action.payload;
      state.currentUser = { ...user, role: normalizeRole(user.role) };
      state.accessToken = action.payload.accessToken;
      state.isAuthed = true;
    },
    tokenRefreshed(state, action: PayloadAction<{ accessToken: string }>) {
      state.accessToken = action.payload.accessToken;
    },
    sessionEnded(state) {
      state.currentUser = null;
      state.accessToken = null;
      state.isAuthed = false;
      state.onboardingComplete = true;
    },
    onboardingFlagSet(state, action: PayloadAction<{ complete: boolean }>) {
      state.onboardingComplete = action.payload.complete;
    },
    currentUserUpdated(state, action: PayloadAction<Partial<CurrentUser>>) {
      if (!state.currentUser) return;
      state.currentUser = { ...state.currentUser, ...action.payload };
      if (action.payload.role) state.currentUser.role = normalizeRole(action.payload.role);
    },
  },
});

export const {
  sessionStarted,
  tokenRefreshed,
  sessionEnded,
  onboardingFlagSet,
  currentUserUpdated,
} = authSlice.actions;
export const authReducer = authSlice.reducer;

// Selectors (DIP: páginas consomem só selectors, nunca shape do state direto).
export const selectAuthState = (s: { auth: AuthState }): AuthState => s.auth;
export const selectCurrentUser = (s: { auth: AuthState }): CurrentUser | null => s.auth.currentUser;
export const selectIsAuthed = (s: { auth: AuthState }): boolean => s.auth.isAuthed;
export const selectRole = (s: { auth: AuthState }): Role | null => s.auth.currentUser?.role ?? null;
export const selectAvatarTone = (s: { auth: AuthState }): AvatarTone =>
  s.auth.currentUser?.avatarTone ?? 1;
export const selectStoreIds = (s: { auth: AuthState }): string[] =>
  s.auth.currentUser?.storeIds ?? [];
export const selectOnboardingComplete = (s: { auth: AuthState }): boolean =>
  s.auth.onboardingComplete;
